/** @module @liquidefy/code-editor/ai/ai-personalization @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  LanguageId,
  EditorResult,
  PersonalizationState,
  UserProfile,
  UserPreferences,
  CodingStyleProfile,
  BehaviorPattern,
  LearningEvent,
  AdaptationConfig,
  SkillLevel,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  now,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — coding events, suggestions, patterns, recommendations
// ═══════════════════════════════════════════════════════════════════════════════

/** A discrete coding event recorded for behavioral profiling. */
export interface CodingEvent {
  readonly type:
    | 'completion-accepted'
    | 'completion-rejected'
    | 'snippet-used'
    | 'refactor-applied'
    | 'diagnostic-fixed'
    | 'test-written'
    | 'comment-written'
    | 'file-opened'
    | 'search-performed'
    | 'shortcut-used'
    | 'ai-suggestion-accepted'
    | 'ai-suggestion-rejected';
  readonly languageId?: LanguageId;
  readonly context: string;
  readonly timestamp: number;
  readonly metadata?: Record<string, unknown>;
}

/** A personalized suggestion ranked by relevance to the user's profile. */
export interface PersonalizedSuggestion {
  readonly id: string;
  readonly type: 'snippet' | 'refactor' | 'completion' | 'documentation' | 'learning';
  readonly title: string;
  readonly description: string;
  readonly relevanceScore: number; // 0–1
  readonly languageId?: LanguageId;
  readonly source: 'profile' | 'pattern' | 'history';
}

/** A detected workflow pattern in the user's recent activity. */
export interface WorkflowPattern {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly frequency: number;
  readonly confidence: number;
  readonly languageIds: LanguageId[];
  readonly detectedAt: number;
}

/** A learning recommendation generated from competency gaps. */
export interface LearningRecommendation {
  readonly id: string;
  readonly topic: string;
  readonly description: string;
  readonly languageId?: LanguageId;
  readonly priority: 'low' | 'medium' | 'high';
  readonly estimatedMinutes: number;
  readonly reason: string;
}

/** Context passed when requesting personalized suggestions. */
export interface SuggestionContext {
  readonly languageId?: LanguageId;
  readonly currentFile?: string;
  readonly recentActions?: CodingEvent['type'][];
  readonly cursorContext?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY — create and reset
// ═══════════════════════════════════════════════════════════════════════════════

/** Default coding style profile. */
const DEFAULT_CODING_STYLE: CodingStyleProfile = {
  naming: 'camelCase',
  indentation: 'spaces-2',
  quoteStyle: 'single',
  semicolons: true,
  trailingComma: 'es5',
  lineLength: 100,
};

/** Default user preferences. */
const DEFAULT_USER_PREFERENCES: UserPreferences = {
  verbosity: 'standard',
  explainCode: true,
  suggestTests: true,
  autoComplete: true,
};

/** Default adaptation config. */
const DEFAULT_ADAPTATION_CONFIG: AdaptationConfig = {
  learningRate: 0.1,
  adaptationSpeed: 'medium',
  privacyLevel: 'standard',
};

/**
 * Create a fresh PersonalizationState for a new user.
 * Competencies start empty — they accumulate through `updateSkillAssessment`.
 *
 * @param userId The unique identifier for the user.
 */
export function createPersonalizationState(userId: string): PersonalizationState {
  if (!userId.trim()) {
    // Return a sensible default rather than throwing
    userId = generateId('user');
  }

  const profile: UserProfile = {
    codingStyle: DEFAULT_CODING_STYLE,
    skillLevel: 'intermediate',
    preferences: DEFAULT_USER_PREFERENCES,
    competencies: {},
  };

  return {
    userId,
    profile,
    behaviorPatterns: [],
    learningHistory: [],
    adaptationConfig: DEFAULT_ADAPTATION_CONFIG,
  };
}

/**
 * Reset a PersonalizationState to its initial (blank) form.
 * All learned patterns, history, and competency scores are cleared.
 * The userId is preserved.
 *
 * @param state Current personalization state.
 */
export function resetPersonalization(state: PersonalizationState): PersonalizationState {
  return createPersonalizationState(state.userId);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 EVENT RECORDING — update behavioral profile from coding events
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Record a coding event into the personalization state.
 * The event is stored in learning history and may update behavior patterns
 * based on frequency analysis.
 *
 * @param state Current personalization state.
 * @param event The coding event to record.
 */
export function recordCodingEvent(
  state: PersonalizationState,
  event: CodingEvent,
): EditorResult<PersonalizationState> {
  const learningEvent: LearningEvent = {
    type: event.type,
    context: event.context,
    outcome: deriveOutcome(event.type),
    timestamp: event.timestamp,
  };

  const learningHistory = [...state.learningHistory, learningEvent];

  // Trim history to the last 500 events to avoid unbounded growth
  const trimmedHistory = learningHistory.length > 500
    ? learningHistory.slice(-500)
    : learningHistory;

  // Update behavior pattern frequencies
  const behaviorPatterns = updateBehaviorPatterns(state.behaviorPatterns, event);

  return ok(
    { ...state, learningHistory: trimmedHistory, behaviorPatterns },
    'CODING_EVENT_RECORDED',
    `Recorded event: ${event.type}`,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 SKILL ASSESSMENT — track competency by language
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Update the competency score for a given language.
 * Scores are in the range 0–100. The overall skill level is recomputed
 * from the average competency across all tracked languages.
 *
 * @param state    Current personalization state.
 * @param language The language ID to update.
 * @param score    The new competency score (0–100).
 */
export function updateSkillAssessment(
  state: PersonalizationState,
  language: LanguageId,
  score: number,
): EditorResult<PersonalizationState> {
  if (score < 0 || score > 100) {
    return err('INVALID_SCORE', `Score must be between 0 and 100, got: ${score}`);
  }

  const competencies = { ...state.profile.competencies, [language]: score };
  const skillLevel = deriveSkillLevel(competencies);
  const profile: UserProfile = { ...state.profile, competencies, skillLevel };

  return ok(
    { ...state, profile },
    'SKILL_ASSESSMENT_UPDATED',
    `Competency for ${language} updated to ${score}`,
  );
}

/**
 * Get the skill level for a specific language.
 * Returns a SkillLevel enum value based on the stored competency score.
 *
 * @param state    Current personalization state.
 * @param language The language to query.
 */
export function getCompetencyLevel(
  state: PersonalizationState,
  language: LanguageId,
): SkillLevel {
  const score = state.profile.competencies[language] ?? 0;
  return scoreToSkillLevel(score);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 PERSONALIZED SUGGESTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generate a ranked list of personalized suggestions for the current editor context.
 * Suggestions are derived from:
 * - The user's competency profile (surface educational content for gaps)
 * - Behavioral patterns (surface relevant workflow shortcuts)
 * - Recent history (avoid repeating rejected suggestions)
 *
 * @param state   Current personalization state.
 * @param context The current editor context (language, file, recent actions).
 */
export function getPersonalizedSuggestions(
  state: PersonalizationState,
  context: SuggestionContext,
): PersonalizedSuggestion[] {
  const suggestions: PersonalizedSuggestion[] = [];

  // Language-specific competency gap suggestions
  if (context.languageId) {
    const score = state.profile.competencies[context.languageId] ?? 0;
    if (score < 50) {
      suggestions.push({
        id: generateId('sug'),
        type: 'learning',
        title: `Improve ${context.languageId} proficiency`,
        description: `Your ${context.languageId} score is ${score}/100. Consider reviewing core patterns.`,
        relevanceScore: 0.9,
        languageId: context.languageId,
        source: 'profile',
      });
    }
  }

  // Pattern-based suggestions
  for (const pattern of state.behaviorPatterns) {
    if (pattern.frequency > 5 && pattern.confidence > 0.6) {
      const relevance = Math.min(1, (pattern.frequency / 20) * pattern.confidence);
      suggestions.push({
        id: generateId('sug'),
        type: 'snippet',
        title: `Automate: ${pattern.pattern}`,
        description: `You do this frequently — consider a snippet or macro.`,
        relevanceScore: relevance,
        source: 'pattern',
      });
    }
  }

  // Verbosity preference suggestions
  if (state.profile.preferences.suggestTests && context.languageId) {
    suggestions.push({
      id: generateId('sug'),
      type: 'documentation',
      title: 'Add tests for current module',
      description: 'Based on your preferences, you prefer test coverage.',
      relevanceScore: 0.7,
      languageId: context.languageId,
      source: 'profile',
    });
  }

  // Sort by relevance descending
  return suggestions.sort((a, b) => b.relevanceScore - a.relevanceScore).slice(0, 10);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 PATTERN DETECTION — identify workflow patterns from events
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Analyze recent coding events to detect recurring workflow patterns.
 * Returns detected patterns sorted by confidence descending.
 *
 * @param state        Current personalization state.
 * @param recentEvents A window of recent coding events to analyze.
 */
export function detectCodingPattern(
  state: PersonalizationState,
  recentEvents: CodingEvent[],
): WorkflowPattern[] {
  const patterns: WorkflowPattern[] = [];

  // Count event type frequencies
  const freq = new Map<string, number>();
  for (const event of recentEvents) {
    freq.set(event.type, (freq.get(event.type) ?? 0) + 1);
  }

  const total = recentEvents.length || 1;

  for (const [type, count] of freq) {
    const confidence = count / total;
    if (confidence > 0.2 && count > 2) {
      const languages = [...new Set(
        recentEvents.filter(e => e.type === type && e.languageId).map(e => e.languageId as LanguageId),
      )];

      patterns.push({
        id: generateId('pat'),
        name: formatPatternName(type),
        description: `Frequent action: ${type} (${count}x in recent session)`,
        frequency: count,
        confidence,
        languageIds: languages,
        detectedAt: now(),
      });
    }
  }

  return patterns.sort((a, b) => b.confidence - a.confidence);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 LEARNING RECOMMENDATIONS — generate from competency gaps
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generate a prioritized list of learning recommendations based on the user's
 * current competency profile and behavioral history.
 *
 * @param state Current personalization state.
 */
export function generateLearningRecommendations(
  state: PersonalizationState,
): LearningRecommendation[] {
  const recommendations: LearningRecommendation[] = [];

  for (const [language, score] of Object.entries(state.profile.competencies)) {
    if (score < 30) {
      recommendations.push({
        id: generateId('rec'),
        topic: `${language} fundamentals`,
        description: `Core syntax, patterns, and idioms for ${language}.`,
        languageId: language as LanguageId,
        priority: 'high',
        estimatedMinutes: 60,
        reason: `Competency score is low (${score}/100)`,
      });
    } else if (score < 60) {
      recommendations.push({
        id: generateId('rec'),
        topic: `Intermediate ${language} patterns`,
        description: `Advanced patterns, performance, and ecosystem tools for ${language}.`,
        languageId: language as LanguageId,
        priority: 'medium',
        estimatedMinutes: 45,
        reason: `Competency score is moderate (${score}/100)`,
      });
    }
  }

  // Suggest test writing if they rarely write tests
  const testEvents = state.learningHistory.filter(e => e.type === 'test-written').length;
  const totalEvents = state.learningHistory.length;
  if (totalEvents > 20 && testEvents / totalEvents < 0.05) {
    recommendations.push({
      id: generateId('rec'),
      topic: 'Test-driven development practices',
      description: 'You write tests infrequently — explore TDD workflow.',
      priority: 'medium',
      estimatedMinutes: 30,
      reason: 'Low test writing frequency in history',
    });
  }

  return recommendations.sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return order[a.priority] - order[b.priority];
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 PREFERENCES — update explicit user preferences
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Update user preferences with the given partial override.
 * Only provided fields are changed — all others are preserved.
 *
 * @param state       Current personalization state.
 * @param preferences Partial preferences to merge.
 */
export function updatePreferences(
  state: PersonalizationState,
  preferences: Partial<UserPreferences>,
): EditorResult<PersonalizationState> {
  const updatedPreferences: UserPreferences = { ...state.profile.preferences, ...preferences };
  const profile: UserProfile = { ...state.profile, preferences: updatedPreferences };
  return ok(
    { ...state, profile },
    'PREFERENCES_UPDATED',
    'User preferences updated',
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §10 SUMMARY
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get a compact summary of the personalization state for display or diagnostics.
 *
 * @param state Current personalization state.
 */
export function getPersonalizationSummary(state: PersonalizationState): Record<string, unknown> {
  const languageCount = Object.keys(state.profile.competencies).length;
  const avgScore =
    languageCount > 0
      ? Object.values(state.profile.competencies).reduce((a, b) => a + b, 0) / languageCount
      : 0;

  return {
    userId: state.userId,
    skillLevel: state.profile.skillLevel,
    trackedLanguages: languageCount,
    averageCompetencyScore: Math.round(avgScore),
    behaviorPatternCount: state.behaviorPatterns.length,
    learningEventCount: state.learningHistory.length,
    adaptationSpeed: state.adaptationConfig.adaptationSpeed,
    privacyLevel: state.adaptationConfig.privacyLevel,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §11 INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/** Map a competency score to a SkillLevel. */
function scoreToSkillLevel(score: number): SkillLevel {
  if (score < 25) return 'beginner';
  if (score < 55) return 'intermediate';
  if (score < 80) return 'advanced';
  return 'expert';
}

/** Derive the overall skill level from the competency map. */
function deriveSkillLevel(competencies: Record<string, number>): SkillLevel {
  const scores = Object.values(competencies);
  if (scores.length === 0) return 'intermediate';
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  return scoreToSkillLevel(avg);
}

/** Map an event type to the appropriate LearningEvent outcome. */
function deriveOutcome(type: CodingEvent['type']): LearningEvent['outcome'] {
  if (type.includes('accepted') || type.includes('used') || type.includes('applied') || type.includes('written')) {
    return 'accepted';
  }
  if (type.includes('rejected')) return 'rejected';
  return 'accepted';
}

/** Update behavior patterns in-place given a new coding event. */
function updateBehaviorPatterns(
  existing: BehaviorPattern[],
  event: CodingEvent,
): BehaviorPattern[] {
  const existingIdx = existing.findIndex(p => p.pattern === event.type);
  if (existingIdx !== -1) {
    const old = existing[existingIdx]!;
    const updated: BehaviorPattern = {
      ...old,
      frequency: old.frequency + 1,
      confidence: Math.min(1, old.confidence + 0.02),
      lastSeen: event.timestamp,
    };
    return [
      ...existing.slice(0, existingIdx),
      updated,
      ...existing.slice(existingIdx + 1),
    ];
  }
  // New pattern
  const newPattern: BehaviorPattern = {
    type: 'coding',
    pattern: event.type,
    frequency: 1,
    confidence: 0.1,
    lastSeen: event.timestamp,
  };
  return [...existing, newPattern];
}

/** Format an event type string into a human-readable pattern name. */
function formatPatternName(type: string): string {
  return type.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
